import { CalendarIcon, GitBranchIcon, ListTreeIcon } from "lucide-react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { formatWorkItemId } from "~/lib/formatWorkItemId";
import { cn } from "~/lib/utils";
import { PriorityIcon } from "~/components/projects/ProjectsPriorityIcon";
import { deriveTaskPresentation } from "./taskPresentation";
import { deriveSubtaskProgress } from "./subtaskProgress";
import { AgentStatusBadge } from "./atoms/AgentStatusBadge";
import { SubtaskProgress } from "./atoms/SubtaskProgress";
import { TaskAssigneeCluster } from "./atoms/TaskCardOwnership";
import { TaskOwnerIdentity } from "./atoms/TaskOwnerIdentity";
import { TaskStatusGlyph } from "./atoms/TaskStatusGlyph";

export interface ProjectTaskTableRowProps {
  readonly task: CmmdBoardTask;
  readonly threadIndex: ReadonlyMap<string, SidebarThreadSummary>;
  readonly pending: boolean;
  readonly hasConversation: boolean;
  readonly onOpenTask: (taskId: number) => void;
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
  readonly workItemPrefix?: string | null | undefined;
}

/**
 * One task as a Linear-style table row for the shared DataTable. The whole row
 * opens the detail drawer; the agent badge is an action control that stops
 * propagation so it opens the executing thread instead of the drawer. Identity
 * (prio + ID + status + title + chips) lives in the Issue column; assignees,
 * owner, and due trail as narrow columns.
 */
export function ProjectTaskTableRow({
  task,
  threadIndex,
  pending,
  hasConversation,
  onOpenTask,
  onOpenThread,
  workItemPrefix,
}: ProjectTaskTableRowProps) {
  const p = deriveTaskPresentation(task, threadIndex);
  const progress = deriveSubtaskProgress(task);
  const workItemId = formatWorkItemId(workItemPrefix, task.id);

  return (
    <tr
      onClick={() => onOpenTask(task.id)}
      className={cn(
        "group cursor-pointer shadow-[inset_0_-1px_0_hsl(var(--border)/0.55)] transition-colors duration-150 last:shadow-none hover:bg-muted",
        pending && "opacity-60",
      )}
    >
      {/* Issue — priority → ID → status → title → inline chips / progress. */}
      <td className="px-3 py-1.5 md:py-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <PriorityIcon priority={task.priority} />
          <span className="shrink-0 font-mono text-meta tabular-nums text-muted-foreground md:text-[11px]">
            {workItemId}
          </span>
          <TaskStatusGlyph status={task.status} />
          {/* DataTable swaps the mobile body for this table at `sm` (640px) but the
              touch-density handoff is `md` (768px), so between the two the title
              would inherit the table's `text-sm` and fall under the mobile reading
              minimum while the row around it is still a touch target. */}
          <span className="min-w-0 flex-1 truncate text-read text-foreground/90 md:text-sm">
            {task.title}
          </span>
          {p.isBlocked ? (
            <span className="shrink-0 text-meta text-hue-rose-foreground md:text-[11px]">
              Blocked
            </span>
          ) : null}
          {progress != null ? (
            <SubtaskProgress progress={progress} />
          ) : p.subtaskCount > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-meta text-muted-foreground md:text-[11px]">
              <ListTreeIcon className="size-3" aria-hidden />
              {p.subtaskCount}
            </span>
          ) : null}
          {p.resolvedThread ? (
            <AgentStatusBadge
              agentStatus={p.resolvedThread.agentStatus}
              agentProvider={p.resolvedThread.agentProvider}
              onClick={onOpenThread ? () => onOpenThread(task.id) : undefined}
              className="shrink-0"
            />
          ) : hasConversation || task.threadId !== null ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-meta text-muted-foreground md:text-[11px]">
              <GitBranchIcon className="size-3" aria-hidden />
              History
            </span>
          ) : null}
        </div>
      </td>

      {/* Assignees — the assigned subject plus every agent actively working it,
          the same cluster the board card uses so the two surfaces agree. */}
      <td className="px-2 py-1.5 md:py-1">
        <TaskAssigneeCluster
          task={task}
          fallback={<span className="text-meta text-muted-foreground/50 md:text-xs">—</span>}
        />
      </td>

      {/* Owner — accountability, not work. Read-only here; it is edited in the
          detail rail, like every other field on this row. */}
      <td className="px-2 py-1.5 md:py-1">
        <TaskOwnerIdentity name={task.ownerName} avatarUrl={task.ownerAvatar} />
      </td>

      {/* Due */}
      <td className="whitespace-nowrap px-2 py-1.5 md:py-1">
        {p.dueLabel ? (
          <span className="inline-flex items-center gap-1 font-mono text-meta tabular-nums text-muted-foreground/80 md:text-[11px]">
            <CalendarIcon className="size-3" aria-hidden />
            {p.dueLabel}
          </span>
        ) : (
          <span className="text-meta text-muted-foreground/50 md:text-xs">—</span>
        )}
      </td>
    </tr>
  );
}
